import { useCallback, useLayoutEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import {
  CURRENCY_CONFIGURATION_LIST_API_URL,
  CURRENCY_CONFIGURATION_SAVE_API_URL,
  CURRENCY_CONFIGURATION_PROJECT_API_URL,
  BUSINESS_AREA_SETTINGS_API_URL,
} from "../../config/constants";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { apiFetch } from "../../utils/apiClient";
import DataState from "../../components/DataState/DataState";

const { Text } = Typography;





const PREFIX_PATTERN = /^[A-Za-z]{4}$/;

const mapMappings = (data) => {
  if (!data || typeof data !== "object") return [];

  const root = data;
  const rawList = Array.isArray(root.projectMappings)
    ? root.projectMappings
    : Array.isArray(data)
      ? (data)
      : [];

  return rawList
    .map((row) => {
      if (!row || typeof row !== "object") return null;

      const projectPrefix = String(row.projectPrefix ?? "").trim();
      const usdConversionFactor = Number(row.usdConversionFactor ?? 0) || 0;

      if (!projectPrefix) return null;

      return {
        projectPrefix,
        usdConversionFactor,
        active: row.active !== false,
        updatedBy: row.updatedBy,
        updatedDate: row.updatedDate,
      };
    })
    .filter((entry) => entry !== null)
    .filter((entry) => entry.active);
};







const FIELD_OPTIONS_CONFIG = [
  { key: "businessArea", label: "Business area" },
];

const FIELD_PREFIX = "field:";

const tableWrapStyle = {
  overflowX: "auto",
  minHeight: 80,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#ffffff",
};

const thStyle = {
  border: "1px solid #e6e6e6",
  padding: "8px 10px",
  background: "#f5f5f5",
  fontWeight: 600,
  fontSize: 12,
  color: "#595959",
  textAlign: "left",
};

const tdStyle = {
  border: "1px solid #e6e6e6",
  padding: "6px 10px",
  verticalAlign: "middle",
  fontSize: 13,
};

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: "#8c8c8c",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  display: "block",
  padding: "4px 10px",
};

const navItemBaseStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "8px 10px",
  borderRadius: 8,
  fontSize: 12,
  color: "#262626",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  marginBottom: 2,
};

const navItemActiveStyle = {
  background: "#eff4ff",
  color: "#1677ff",
  fontWeight: 500,
};

const getAuthHeader = () => {
  const rawToken =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");
  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

  if (!rawToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }

  const normalizedToken = rawToken.replace(/^"|"$/g, "").replace(/^Bearer\s+/i, "").trim();
  return `${tokenType} ${normalizedToken}`;
};

const mapBusinessAreasToOptions = (data) => {
  if (!Array.isArray(data)) return [];

  return data
    .map((item) => {
      const row = item;

      if (!row.domainId || !row.domainName) return null;

      return {
        id: row.domainId,
        name: row.domainName,
        active: row.active,
        createdBy: row.createdBy,
        createdOn: row.createdOn,
        updatedBy: row.updatedBy,
        updatedOn: row.updatedOn,
      };
    })
    .filter((item) => item !== null);
};

export function ConfigurationPage() {
 
   
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState("currency:all");

  const isCurrencySection = activeSection.startsWith("currency:");
  const isFieldSection = activeSection.startsWith(FIELD_PREFIX);

  const selectedField = isFieldSection
    ? activeSection.slice(FIELD_PREFIX.length)
    : FIELD_OPTIONS_CONFIG[0].key;

  const [mappings, setMappings] = useState([]);
  const [mappingsLoaded, setMappingsLoaded] = useState(false);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsError, setMappingsError] = useState("");

  const [currencyModalOpen, setCurrencyModalOpen] = useState(false);
  const [currencyModalMode, setCurrencyModalMode] = useState("add");
  const [currencyModalSaving, setCurrencyModalSaving] = useState(false);
  const [editingPrefix, setEditingPrefix] = useState(null);
  const [deletingPrefix, setDeletingPrefix] = useState(null);
  const [currencyForm] = Form.useForm();

  const mappingsStatus =
    mappingsLoading || !mappingsLoaded
      ? "loading"
      : mappingsError
        ? "error"
        : "ready";

  const fetchMappings = useCallback(async () => {
    setMappingsLoading(true);
    setMappingsError("");
    try {
      const authHeader = getAuthHeader();
      const res = await apiFetch(CURRENCY_CONFIGURATION_LIST_API_URL, {
        method: "GET",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }, "Unable to load currency configuration.");

      if (!res.ok) {
        setMappings([]);
        setMappingsError("Unable to load currency configuration.");
        return;
      }

      const data = await res.json();
      setMappings(mapMappings(data));
    } catch (error) {
      setMappings([]);
      setMappingsError(
        error instanceof Error && error.message
          ? error.message
          : "Please check your connection and try again.",
      );
    } finally {
      setMappingsLoading(false);
      setMappingsLoaded(true);
    }
  }, []);


  // useLayoutEffect: the loading flag is set before paint, so a section switch never shows the previous data.
  useLayoutEffect(() => {
    if (isCurrencySection) {
      void fetchMappings();
    }
  }, [isCurrencySection, fetchMappings]);

  const openAddModal = () => {
    setCurrencyModalMode("add");
    setEditingPrefix(null);
    currencyForm.resetFields();
    setCurrencyModalOpen(true);
  };

  const openEditModal = (record) => {
    setCurrencyModalMode("edit");
    setEditingPrefix(record.projectPrefix);
    currencyForm.setFieldsValue({
      projectPrefix: record.projectPrefix,
      usdConversionFactor: record.usdConversionFactor,
    });
    setCurrencyModalOpen(true);
  };

  const closeCurrencyModal = () => {
    if (currencyModalSaving) return;
    setCurrencyModalOpen(false);
    setEditingPrefix(null);
    currencyForm.resetFields();
  };

  const saveMapping = async (
    method,
    payload,
  ) => {
    const authHeader = getAuthHeader();

    const res = await apiFetch(CURRENCY_CONFIGURATION_SAVE_API_URL, {
      method,
      headers: {
        Authorization: authHeader,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    }, "Failed to save currency configuration.", { passthrough: [400] });

    if (!res.ok) {
      let errorMessage = `Failed to save currency configuration (${res.status})`;

      try {
        const data = (await res.json());
        if (data?.message) errorMessage = data.message;
      } catch {
      }

      throw new Error(errorMessage);
    }
  };

  const deleteMappingRequest = async (projectPrefix) => {
    const authHeader = getAuthHeader();

    const res = await apiFetch(`${CURRENCY_CONFIGURATION_PROJECT_API_URL}/${projectPrefix}`, {
      method: "DELETE",
      headers: {
        Authorization: authHeader,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }, "Failed to delete currency configuration.", { passthrough: [400] });

    if (!res.ok) {
      let errorMessage = `Failed to delete currency configuration (${res.status})`;

      try {
        const data = (await res.json()) ;
        if (data?.message) errorMessage = data.message;
      } catch {
      }

      throw new Error(errorMessage);
    }
  };

  const handleCurrencyModalSubmit = async () => {
    let values;

    try {
      values = await currencyForm.validateFields();
    } catch {
      return;
    }

    setCurrencyModalSaving(true);

    const projectPrefix = values.projectPrefix.trim().toUpperCase();

    try {
      if (currencyModalMode === "add") {
        const alreadyExists = mappings.some((m) => m.projectPrefix === projectPrefix);
        if (alreadyExists) {
          message.warning("That project prefix already exists.");
          setCurrencyModalSaving(false);
          return;
        }

        await saveMapping("POST", {
          projectPrefix,
          usdConversionFactor: values.usdConversionFactor,
        });

        message.success("Currency configuration added successfully.");
      } else {
        const prefixChanged =
          editingPrefix !== null && editingPrefix !== projectPrefix;

        if (prefixChanged) {
          const alreadyExists = mappings.some((m) => m.projectPrefix === projectPrefix);
          if (alreadyExists) {
            message.warning("That project prefix already exists.");
            setCurrencyModalSaving(false);
            return;
          }

          await saveMapping("POST", {
            projectPrefix,
            usdConversionFactor: values.usdConversionFactor,
          });
          await deleteMappingRequest(editingPrefix);
        } else {
          await saveMapping("PUT", {
            projectPrefix,
            usdConversionFactor: values.usdConversionFactor,
          });
        }

        message.success("Currency configuration updated successfully.");
      }

      setCurrencyModalOpen(false);
      setEditingPrefix(null);
      currencyForm.resetFields();
      await fetchMappings();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to save currency configuration.";
      message.error(errorMessage);
    } finally {
      setCurrencyModalSaving(false);
    }
  };

  const handleDeleteMapping = async (projectPrefix) => {
    setDeletingPrefix(projectPrefix);

    try {
      await deleteMappingRequest(projectPrefix);
      message.success("Currency configuration deleted successfully.");
      await fetchMappings();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to delete currency configuration.";
      message.error(errorMessage);
    } finally {
      setDeletingPrefix(null);
    }
  };


  const [fieldOptions, setFieldOptions] = useState({});
  const [fieldOptionsLoading, setFieldOptionsLoading] = useState(false);
  const [fieldOptionsLoadedFor, setFieldOptionsLoadedFor] = useState({});
  const [businessAreasError, setBusinessAreasError] = useState("");
  const [newOptionValue, setNewOptionValue] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const [addingOption, setAddingOption] = useState(false);
  const [updatingOptionId, setUpdatingOptionId] = useState(null);
  const [deletingOptionId, setDeletingOptionId] = useState(null);

  const currentOptions = fieldOptions[selectedField] || [];
  const currentFieldLoaded = Boolean(fieldOptionsLoadedFor[selectedField]);
  const businessAreasFailed = selectedField === "businessArea" && Boolean(businessAreasError);

  const businessAreasStatus =
    fieldOptionsLoading || !currentFieldLoaded
      ? "loading"
      : businessAreasError
        ? "error"
        : "ready";

  const fetchFieldOptions = useCallback(async (fieldKey) => {
    setFieldOptionsLoading(true);

    try {
      if (fieldKey !== "businessArea") {
        setFieldOptions((prev) => ({ ...prev, [fieldKey]: [] }));
        return;
      }

      setBusinessAreasError("");

      const authHeader = getAuthHeader();

      const res = await apiFetch(BUSINESS_AREA_SETTINGS_API_URL, {
        method: "GET",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }, "Failed to load business areas.");

      if (!res.ok) {
        setFieldOptions((prev) => ({ ...prev, [fieldKey]: [] }));
        setBusinessAreasError("Unable to load business areas.");
        return;
      }

      const data = await res.json();
      const options = mapBusinessAreasToOptions(data);

      setFieldOptions((prev) => ({
        ...prev,
        [fieldKey]: options,
      }));
    } catch (error) {
      setFieldOptions((prev) => ({ ...prev, [fieldKey]: [] }));
      setBusinessAreasError(
        error instanceof Error && error.message
          ? error.message
          : "Please check your connection and try again.",
      );
    } finally {
      setFieldOptionsLoading(false);
      setFieldOptionsLoadedFor((prev) => ({ ...prev, [fieldKey]: true }));
    }
  }, []);

  useLayoutEffect(() => {
    if (isFieldSection) {
      void fetchFieldOptions(selectedField);
    }
  }, [isFieldSection, selectedField, fetchFieldOptions]);

  const handleAddOption = async () => {
    const trimmed = newOptionValue.trim();

    if (!trimmed) return;

    const alreadyExists = currentOptions.some(
      (opt) => opt.name.toLowerCase() === trimmed.toLowerCase(),
    );

    if (alreadyExists) {
      message.warning("That business area already exists.");
      return;
    }

    setAddingOption(true);

    try {
      const authHeader = getAuthHeader();

      const body = {
        domainName: trimmed,
      };

      const res = await apiFetch(BUSINESS_AREA_SETTINGS_API_URL, {
        method: "POST",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      }, "Failed to create business area.", { passthrough: [400] });

      if (!res.ok) {
        let errorMessage = `Failed to create business area (${res.status})`;

        try {
          const data = (await res.json());
          if (data?.message) errorMessage = data.message;
        } catch {
          console.log("error parsing error response for creating business area");
        }

        throw new Error(errorMessage);
      }

      message.success("Business area added successfully.");
      setNewOptionValue("");
      await fetchFieldOptions(selectedField);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to add business area.";

      message.error(errorMessage);
    } finally {
      setAddingOption(false);
    }
  };

  const handleDeleteOption = async (id) => {
    setDeletingOptionId(id);

    try {
      const authHeader = getAuthHeader();

      const res = await apiFetch(`${BUSINESS_AREA_SETTINGS_API_URL}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }, "Failed to delete business area.", { passthrough: [400] });

      if (!res.ok) {
        let errorMessage = `Failed to delete business area (${res.status})`;

        try {
          const data = (await res.json());
          if (data?.message) errorMessage = data.message;
        } catch {
          console.log("error parsing error response for deleting business area");
        }

        throw new Error(errorMessage);
      }

      message.success("Business area deleted successfully.");
      await fetchFieldOptions(selectedField);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to delete business area.";

      message.error(errorMessage);
    } finally {
      setDeletingOptionId(null);
    }
  };

  const startEditing = (option) => {
    setEditingId(option.id);
    setEditingValue(option.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const commitEditing = async () => {
    const trimmed = editingValue.trim();

    if (!editingId) return;

    if (updatingOptionId) return;

    if (!trimmed) {
      message.warning("Business area name is required.");
      return;
    }

    const currentItem = currentOptions.find((opt) => opt.id === editingId);

    if (!currentItem) {
      cancelEditing();
      return;
    }

    if (currentItem.name.trim() === trimmed) {
      cancelEditing();
      return;
    }

    const alreadyExists = currentOptions.some(
      (opt) => opt.id !== editingId && opt.name.toLowerCase() === trimmed.toLowerCase(),
    );

    if (alreadyExists) {
      message.warning("That business area already exists.");
      return;
    }

    setUpdatingOptionId(editingId);

    try {
      const authHeader = getAuthHeader();

      const body = {
        domainName: trimmed,
      };

      const res = await apiFetch(`${BUSINESS_AREA_SETTINGS_API_URL}/${editingId}`, {
        method: "PUT",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      }, "Failed to update business area.", { passthrough: [400] });

      if (!res.ok) {
        let errorMessage = `Failed to update business area(${res.status})`;

        try {
          const data = (await res.json()) ;
          if (data?.message) errorMessage = data.message;
        } catch {
          console.log("error parsing error response for updating business area");
        }

        throw new Error(errorMessage);
      }

      message.success("Business area updated successfully.");
      cancelEditing();
      await fetchFieldOptions(selectedField);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to update business area.";

      message.error(errorMessage);
    } finally {
      setUpdatingOptionId(null);
    }
  };

  const goToSection = (section) => {
    setActiveSection(section);
    setEditingId(null);
    setEditingValue("");
    setNewOptionValue("");
  };

  const navItem = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => goToSection(key)}
      style={{
        ...navItemBaseStyle,
        ...(activeSection === key ? navItemActiveStyle : {}),
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        background: "linear-gradient(180deg, #f7fbff 0%, #ffffff 35%)",
        borderRadius: 16,
        padding: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Space size={10}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/watchtower/settings")}
            style={{
              borderRadius: 10,
              border: "1px solid #e6f4ff",
            }}
          />
        </Space>
      </div>

      <Card
        bordered={false}
        style={{
          borderRadius: 16,
          boxShadow: "0 12px 28px rgba(23, 43, 77, 0.08)",
        }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ display: "flex", minHeight: 420 }}>
          {/* Sidebar */}
          <div
            style={{
              width: 220,
              borderRight: "1px solid #e6edf5",
              padding: "16px 10px",
            }}
          >
            <Text style={sectionLabelStyle}>Configuration</Text>
            {navItem("currency:all", "Currency")}

            {FIELD_OPTIONS_CONFIG.map((field) =>
              navItem(`${FIELD_PREFIX}${field.key}`, field.label),
            )}
          </div>

          {/* Detail pane */}
          <div style={{ flex: 1, padding: "18px 24px" }}>
            {isCurrencySection && (
              <DataState
                status={mappingsStatus}
                errorTitle="Couldn't load currency configuration"
                error={mappingsError}
                onRetry={() => void fetchMappings()}
                skeletonRows={6}
              >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 14,
                    maxWidth: 560,
                  }}
                >
                  <Text style={{ fontWeight: 600, fontSize: 14 }}>
                    Currency configuration
                  </Text>

                  {!mappingsError && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openAddModal}
                    style={{ borderRadius: "4px", backgroundColor: "#0958d9", fontWeight: 450, height: 35 }}
                  >
                    Add
                  </Button>
                  )}
                </div>

                <div style={{ ...tableWrapStyle, maxWidth: 560 }}>
                  <Spin spinning={mappingsLoading}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={{ ...thStyle, width: "40%" }}>Project prefix</th>
                            <th style={thStyle}>USD conversion factor</th>
                            <th
                              style={{
                                ...thStyle,
                                width: 90,
                                textAlign: "center",
                              }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {mappings.length === 0 ? (
                            <tr>
                              <td style={tdStyle} colSpan={3}>
                                <Text style={{ color: "#8c8c8c", fontSize: 13 }}>
                                  No currency configurations added yet.
                                </Text>
                              </td>
                            </tr>
                          ) : (
                            mappings.map((row) => (
                              <tr key={row.projectPrefix}>
                                <td style={tdStyle}>{row.projectPrefix}</td>
                                <td style={tdStyle}>{row.usdConversionFactor}</td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    width: 90,
                                    textAlign: "center",
                                  }}
                                >
                                  <Space size={2}>
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
                                      disabled={Boolean(deletingPrefix)}
                                      onClick={() => openEditModal(row)}
                                    />

                                    <Popconfirm
                                      overlayClassName="trackon-root"
                                      title="Delete this currency configuration?"
                                      description="This will deactivate it and it will no longer appear"
                                      okText="Delete"
                                      okButtonProps={{ danger: true }}
                                      cancelText="Cancel"
                                      onConfirm={() => {
                                        void handleDeleteMapping(row.projectPrefix);
                                      }}
                                    >
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={deletingPrefix === row.projectPrefix}
                                      />
                                    </Popconfirm>
                                  </Space>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </Spin>
                </div>
              </div>
              </DataState>
            )}

            {isFieldSection && (
              <DataState
                status={businessAreasStatus}
                errorTitle="Couldn't load business areas"
                error={businessAreasError}
                onRetry={() => {
                  void fetchFieldOptions("businessArea");
                }}
                skeletonRows={4}
              >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 14,
                  }}
                >
                  <Text style={{ fontWeight: 600, fontSize: 14 }}>
                    {
                      FIELD_OPTIONS_CONFIG.find((field) => field.key === selectedField)?.label
                    }{" "}
                    options
                  </Text>

                  {!businessAreasFailed && (
                  <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {currentOptions.length} options
                  </Text>
                  )}
                </div>

                {!businessAreasFailed && (
                <Space.Compact
                  style={{
                    width: "100%",
                    maxWidth: 460,
                    marginBottom: 14,
                  }}
                >
                  <Input
                    value={newOptionValue}
                    onChange={(e) => setNewOptionValue(e.target.value)}
                    onPressEnter={() => {
                      void handleAddOption();
                    }}
                    placeholder="Type a new business area"
                    disabled={addingOption}
                  />

                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      void handleAddOption();
                    }}
                    loading={addingOption}
                    disabled={!newOptionValue.trim()}
                  >
                    Add
                  </Button>
                </Space.Compact>
                )}

                <div style={{ ...tableWrapStyle, maxWidth: 520 }}>
                  <Spin spinning={fieldOptionsLoading}>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Business Area</th>
                            <th
                              style={{
                                ...thStyle,
                                width: 90,
                                textAlign: "center",
                              }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {currentOptions.length === 0 ? (
                            <tr>
                              <td style={tdStyle} colSpan={2}>
                                <Text style={{ color: "#8c8c8c", fontSize: 13 }}>
                                  No business Areas configured yet.
                                </Text>
                              </td>
                            </tr>
                          ) : (
                            currentOptions.map((option) => (
                              <tr key={option.id}>
                                <td style={tdStyle}>
                                  {editingId === option.id ? (
                                    <Input
                                      size="small"
                                      autoFocus
                                      value={editingValue}
                                      disabled={updatingOptionId === option.id}
                                      onChange={(e) => setEditingValue(e.target.value)}
                                      onPressEnter={() => {
                                        void commitEditing();
                                      }}
                                      onBlur={() => {
                                        void commitEditing();
                                      }}
                                    />
                                  ) : (
                                    <span>{option.name}</span>
                                  )}
                                </td>

                                <td
                                  style={{
                                    ...tdStyle,
                                    width: 90,
                                    textAlign: "center",
                                  }}
                                >
                                  <Space size={2}>
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
                                      disabled={Boolean(updatingOptionId || deletingOptionId)}
                                      onClick={() => startEditing(option)}
                                    />

                                    <Popconfirm
                                      overlayClassName="trackon-root"
                                      title="Delete this business area?"
                                      description="This will deactivate it and it will no longer appear as an active option."
                                      okText="Delete"
                                      okButtonProps={{ danger: true }}
                                      cancelText="Cancel"
                                      onConfirm={() => {
                                        void handleDeleteOption(option.id);
                                      }}
                                    >
                                      <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={deletingOptionId === option.id}
                                        disabled={Boolean(updatingOptionId)}
                                      />
                                    </Popconfirm>
                                  </Space>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </Spin>
                </div>

                {!businessAreasFailed && (
                <Text
                  style={{
                    display: "block",
                    marginTop: 10,
                    fontSize: 12,
                    color: "#8c8c8c",
                  }}
                >
                  Add, rename, and delete actions are saved immediately.
                </Text>
                )}
              </div>
              </DataState>
            )}
          </div>
        </div>
      </Card>

      <Modal
        title={currencyModalMode === "add" ? "Add currency configuration" : "Edit currency configuration"}
        open={currencyModalOpen}
        onCancel={closeCurrencyModal}
        confirmLoading={currencyModalSaving}
        onOk={() => {
          void handleCurrencyModalSubmit();
        }}
        okText={currencyModalMode === "add" ? "Create" : "Save"}
        maskClosable={!currencyModalSaving}
        closable={!currencyModalSaving}
        cancelButtonProps={{ disabled: currencyModalSaving }}
        destroyOnClose
      >
        <Spin spinning={currencyModalSaving}>
          <Form form={currencyForm} layout="vertical" requiredMark={false}>
            <Form.Item
              label="Project prefix"
              name="projectPrefix"
              rules={[
                { required: true, message: "Project prefix is required." },
                {
                  pattern: PREFIX_PATTERN,
                  message: "Project prefix must be exactly 4 letters.",
                },
              ]}
              normalize={(value) => (value || "").toUpperCase()}
            >
              <Input placeholder="e.g. WMGL" maxLength={4} />
            </Form.Item>

            <Form.Item
              label="USD conversion factor"
              name="usdConversionFactor"
              rules={[
                { required: true, message: "USD conversion factor is required." },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || Number(value) <= 0) {
                      return Promise.reject(new Error("Value must be greater than 0."));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0.0001}
                precision={2}
                controls={false}
                placeholder="Enter USD conversion factor"
              />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
}